// Module-level ref for bridging GoodsItemDetail (RHF) with the
// legacy AppRecord save flow.
//
// Holds a map of edited items keyed by "shipmentIndex:sequence" to support
// multiple goods shipments being edited in the same session.

import type { GoodsItemDetailFormState } from '../schemas/forms/goodsItemDetail';
import type { GoodsItemApiData } from './GoodsItemDetailTransformer';

export interface GoodsItemEntry {
  formData: GoodsItemDetailFormState;
  original: GoodsItemApiData;
  eudrNotApplicable?: boolean;
}

const editedItems = new Map<string, GoodsItemEntry>();

const key = (shipmentIndex: number, sequence: number) =>
  `${shipmentIndex}:${sequence}`;

// Register or update an edited goods item.
export const setGoodsItemEntry = (
  shipmentIndex: number,
  sequence: number,
  entry: GoodsItemEntry,
) => {
  editedItems.set(key(shipmentIndex, sequence), entry);
};

// Remove a single entry (e.g. on discard/reset).
export const clearGoodsItemEntry = (
  shipmentIndex: number,
  sequence: number,
) => {
  editedItems.delete(key(shipmentIndex, sequence));
};

// Clear all entries (e.g. on page unmount).
export const clearAllGoodsItemEntries = () => {
  editedItems.clear();
};

// Get a single entry (e.g. for hydrating defaultValues on re-mount).
export const getGoodsItemEntry = (
  shipmentIndex: number,
  sequence: number,
): GoodsItemEntry | undefined => editedItems.get(key(shipmentIndex, sequence));

// Get every registered entry across all shipments.
export const getAllGoodsItemEntries = (): GoodsItemEntry[] => [
  ...editedItems.values(),
];

// Get edited items for a specific shipment, keyed by sequence.
export const getEditedGoodsItemsForShipment = (
  shipmentIndex: number,
): Map<number, GoodsItemEntry> => {
  const prefix = `${shipmentIndex}:`;
  const result = new Map<number, GoodsItemEntry>();
  for (const [k, v] of editedItems) {
    if (k.startsWith(prefix)) {
      const seq = Number(k.split(':')[1]);
      result.set(seq, v);
    }
  }
  return result;
};
